import { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Page from "./Page";
import EntityList from "./EntityList";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import Input from "../elements/Input";
import Select from "../elements/Select";
import DateField from "../elements/Date";
import Pagination from "../elements/Pagination";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const OrdersSubmissionsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const clientId = location?.state?.clientId || null;
  const clientName = location?.state?.clientName || "";
  const returnPath = location?.state?.returnPath || "/clientorders";
  const returnState = location?.state?.returnState || {};

  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [filterDraft, setFilterDraft] = useState({
    searchText: "",
    poNumber: "",
    transmissionStatus: "all",
    startDate: null,
    endDate: null,
  });
  const [filterApplied, setFilterApplied] = useState({
    searchText: "",
    poNumber: "",
    transmissionStatus: "all",
    startDate: null,
    endDate: null,
  });
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [resubmitting, setResubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [recreatingFileId, setRecreatingFileId] = useState("");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");

  useEffect(() => {
    loadOrders();
  }, [clientId, pageNumber, recordsPerPage, filterApplied]);

  const getSortValue = (row, fieldName) => {
    switch (fieldName) {
      case "recreateFile":
        return row?.latestTransmissionId ? 1 : 0;
      case "callDate":
      case "createdAt": {
        const timestamp = new Date(row?.[fieldName] || 0).getTime();
        return Number.isFinite(timestamp) ? timestamp : 0;
      }
      case "totalQty":
      case "totalAmount":
      case "latestSubmissionVersion":
      case "latestTransmissionAttemptCount": {
        const value = Number(row?.[fieldName]);
        return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
      }
      default:
        return row?.[fieldName] ?? "";
    }
  };

  const compareValues = (leftValue, rightValue) => {
    if (typeof leftValue === "number" && typeof rightValue === "number") {
      return leftValue - rightValue;
    }

    const leftText = String(leftValue ?? "");
    const rightText = String(rightValue ?? "");

    return leftText.localeCompare(rightText, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  const sortedRecords = useMemo(() => {
    const sorted = [...records].sort((left, right) => {
      const result = compareValues(
        getSortValue(left, orderBy),
        getSortValue(right, orderBy),
      );

      if (result !== 0) {
        return order === "asc" ? result : -result;
      }

      // Keep a deterministic order when values are equal.
      if (orderBy !== "createdAt") {
        return (
          new Date(right?.createdAt || 0).getTime() -
          new Date(left?.createdAt || 0).getTime()
        );
      }

      return 0;
    });

    return sorted;
  }, [records, orderBy, order]);

  const selectedRecord = useMemo(() => {
    if (selectedOrderIds.length !== 1) {
      return null;
    }
    return records.find((item) => item.id === selectedOrderIds[0]) || null;
  }, [records, selectedOrderIds]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "Order",
        action: "getResubmissionOrders",
        data: {
          clientId,
          pageNumber,
          recordsPerPage,
          startDate: filterApplied.startDate,
          endDate: filterApplied.endDate,
          filter: filterApplied,
        },
      });

      if (results?.isSuccessful && Array.isArray(results?.data?.records)) {
        setRecords(results.data.records);
        setTotalCount(Number(results?.data?.total || 0));

        // Keep only selected ids visible in current page.
        const validIds = new Set(results.data.records.map((item) => item.id));
        setSelectedOrderIds((previous) =>
          previous.filter((id) => validIds.has(id)),
        );
      } else {
        setRecords([]);
        setTotalCount(0);
        setSelectedOrderIds([]);
        showMessage(results?.message || "Failed to load orders", "error");
      }
    } catch (err) {
      setRecords([]);
      setTotalCount(0);
      setSelectedOrderIds([]);
      showMessage(err?.message || "Failed to load orders", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleToolbarClick = (name) => {
    if (name === "back") {
      navigate(returnPath, { state: returnState });
      return;
    }

    if (name === "refresh") {
      loadOrders();
      return;
    }

    if (name === "resubmit") {
      if (selectedOrderIds.length === 0) {
        showMessage("Select one order first", "error");
        return;
      }

      if (selectedOrderIds.length > 1) {
        showMessage("Select only one order to resubmit", "error");
        return;
      }

      setConfirmOpen(true);
    }
  };

  const handleSelect = (id, checked) => {
    setSelectedOrderIds((previous) => {
      if (!checked) {
        return previous.filter((item) => item !== id);
      }

      if (previous.includes(id)) {
        return previous;
      }

      return [...previous, id];
    });
  };

  const handleSelectAll = (checked) => {
    if (!checked) {
      setSelectedOrderIds([]);
      return;
    }

    setSelectedOrderIds(tableRows.map((item) => item.id));
  };

  const applyFilters = () => {
    const normalizedStart = filterDraft.startDate || null;
    const normalizedEnd = filterDraft.endDate || null;

    if (
      normalizedStart &&
      normalizedEnd &&
      new Date(normalizedStart) > new Date(normalizedEnd)
    ) {
      showMessage("From date cannot be after To date", "error");
      return;
    }

    setPageNumber(0);
    setFilterApplied({
      searchText: String(filterDraft.searchText || "").trim(),
      poNumber: String(filterDraft.poNumber || "").trim(),
      transmissionStatus: filterDraft.transmissionStatus || "all",
      startDate: normalizedStart,
      endDate: normalizedEnd,
    });
  };

  const clearFilters = () => {
    const cleared = {
      searchText: "",
      poNumber: "",
      transmissionStatus: "all",
      startDate: null,
      endDate: null,
    };
    setFilterDraft(cleared);
    setPageNumber(0);
    setFilterApplied(cleared);
  };

  const handleSortChange = (property, nextOrder) => {
    setOrderBy(property);
    setOrder(nextOrder);
  };

  const handleResubmit = async () => {
    if (!selectedRecord?.id) return;

    setResubmitting(true);
    try {
      const isResubmit =
        !!selectedRecord.latestSubmissionId &&
        !!selectedRecord.latestTransmissionId;
      const results = await executeAction({
        entityName: "OrderSubmission",
        action: isResubmit ? "resubmit" : "submitFromOrder",
        data: isResubmit
          ? {
              submissionId: selectedRecord.latestSubmissionId,
              orderId: selectedRecord.id,
              clientId,
            }
          : {
              orderId: selectedRecord.id,
              clientId,
            },
      });

      if (!results?.isSuccessful) {
        showMessage(results?.message || "Failed to resubmit order", "error");
        return;
      }

      showMessage(
        isResubmit
          ? "Order resubmitted and queued"
          : "Order submitted and queued",
        "success",
      );
      setConfirmOpen(false);
      await loadOrders();
    } catch (err) {
      showMessage(err?.message || "Failed to resubmit order", "error");
    } finally {
      setResubmitting(false);
    }
  };

  const downloadRecreatedFile = async (transmissionId) => {
    if (!transmissionId) {
      showMessage("No transmission found for this order", "error");
      return;
    }

    setRecreatingFileId(String(transmissionId));
    try {
      const results = await executeAction({
        entityName: "OrderTransmission",
        action: "recreateSentFile",
        data: { transmissionId: String(transmissionId) },
      });

      if (!results?.isSuccessful || !results?.data?.content) {
        showMessage(results?.message || "Unable to recreate file", "error");
        return;
      }

      const fileName = results?.data?.fileName || "order_recreated.csv";
      const mimeType = results?.data?.mimeType || "text/csv;charset=utf-8;";
      const blob = new Blob([results.data.content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.setAttribute("download", fileName);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showMessage(err?.message || "Unable to recreate file", "error");
    } finally {
      setRecreatingFileId("");
    }
  };

  const tableRows = sortedRecords;

  const formatAmount = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) {
      return "";
    }
    return amount.toFixed(2);
  };

  const transmissionStatusItems = [
    { id: "all", name: "All" },
    { id: "not-submitted", name: "Not Submitted" },
    { id: "queued", name: "Queued" },
    { id: "transmitting", name: "Transmitting" },
    { id: "transmitted", name: "Transmitted" },
    { id: "acknowledged", name: "Acknowledged" },
    { id: "rejected", name: "Rejected" },
    { id: "retry_pending", name: "Retry Pending" },
    { id: "failed_final", name: "Failed Final" },
  ];

  const columns = [
    {
      name: "poNumber",
      label: "PO #",
      width: "140px",
      render: (row) => row?.poNumber || "",
    },
    {
      name: "recreateFile",
      label: "File",
      width: "120px",
      render: (row) => {
        const transmissionId = row?.latestTransmissionId || "";
        const hasTransmission = !!transmissionId;
        const isBusy =
          hasTransmission &&
          recreatingFileId !== "" &&
          recreatingFileId === String(transmissionId);
        return (
          <Button
            size="small"
            variant="outlined"
            disabled={!hasTransmission || isBusy}
            onClick={(event) => {
              event.stopPropagation();
              downloadRecreatedFile(transmissionId);
            }}
          >
            {!hasTransmission
              ? "Not Transmitted"
              : isBusy
                ? "Creating..."
                : "Recreate"}
          </Button>
        );
      },
    },
    {
      name: "locationName",
      label: "Location",
      width: "240px",
      render: (row) => row?.locationName || "",
    },
    {
      name: "repName",
      label: "Rep Name",
      width: "180px",
      render: (row) => row?.repName || "",
    },
    {
      name: "locationNumber",
      label: "Loc #",
      width: "120px",
      render: (row) => row?.locationNumber || "",
    },
    {
      name: "locationCity",
      label: "City",
      width: "140px",
      render: (row) => row?.locationCity || "",
    },
    {
      name: "locationProvince",
      label: "Province",
      width: "110px",
      render: (row) => row?.locationProvince || "",
    },
    {
      name: "callDate",
      label: "Call Date",
      width: "120px",
      render: (row) => row?.callDate || "",
    },
    { name: "totalQty", label: "Qty", width: "90px" },
    {
      name: "totalAmount",
      label: "Amount",
      width: "110px",
      render: (row) => formatAmount(row?.totalAmount),
    },
    {
      name: "latestSubmissionVersion",
      label: "Submission Ver",
      width: "130px",
      render: (row) => row?.latestSubmissionVersion ?? "-",
    },
    {
      name: "latestTransmissionStatus",
      label: "Transmission",
      width: "140px",
      render: (row) => row?.latestTransmissionStatus || "not-submitted",
    },
    {
      name: "latestTransmissionAttemptCount",
      label: "Attempts",
      width: "100px",
      render: (row) => row?.latestTransmissionAttemptCount ?? 0,
    },
    {
      name: "latestTransmissionLastError",
      label: "Last Error",
      width: "360px",
      render: (row) => row?.latestTransmissionLastError || "",
    },
    {
      name: "createdAt",
      label: "Order Created",
      width: "170px",
      render: (row) =>
        row?.createdAt ? new Date(row.createdAt).toLocaleString() : "",
    },
  ];

  return (
    <>
      <Page
        pageName="Order Submissions"
        entityName={clientName || ""}
        includeToolbar
        onToolItemClick={handleToolbarClick}
        items={[
          { name: "back", label: "Back", iconName: "ArrowBack" },
          { name: "refresh", label: "Refresh", iconName: "Refresh" },
          {
            name: "resubmit",
            label: resubmitting ? "Submitting" : "Submit / Resubmit",
            iconName: "Send",
          },
        ]}
      >
        <Box>
          <Typography sx={{ mb: 1 }}>
            Review all orders and resend using the latest submission snapshot.
          </Typography>

          <Typography sx={{ mb: 1, fontSize: 12, color: "text.secondary" }}>
            Selected count: {selectedOrderIds.length}
            {selectedRecord?.localId
              ? ` | Selected order: ${selectedRecord.localId}`
              : ""}
            {selectedRecord?.locationName
              ? ` (${selectedRecord.locationName})`
              : ""}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr auto auto",
              gap: 1,
              alignItems: "end",
              mb: 1,
            }}
          >
            <Input
              label="Search"
              value={filterDraft.searchText}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  searchText: value,
                }))
              }
            />

            <Select
              label="Transmission Status"
              value={filterDraft.transmissionStatus}
              items={transmissionStatusItems}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  transmissionStatus: value,
                }))
              }
            />

            <Input
              label="PO Number"
              value={filterDraft.poNumber}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  poNumber: value,
                }))
              }
            />

            <DateField
              label="From Date"
              value={filterDraft.startDate}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  startDate: value,
                }))
              }
            />

            <DateField
              label="To Date"
              value={filterDraft.endDate}
              onChange={(value) =>
                setFilterDraft((previous) => ({ ...previous, endDate: value }))
              }
            />

            <Button
              variant="outlined"
              onClick={clearFilters}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              onClick={applyFilters}
            >
              Apply
            </Button>
          </Box>

          <Pagination
            totalCount={totalCount}
            pageNumber={pageNumber}
            recordsPerPage={recordsPerPage}
            onPageChange={(newPage) => setPageNumber(newPage)}
            onRecordsPerPageChange={(newRowsPerPage) => {
              setPageNumber(0);
              setRecordsPerPage(Number(newRowsPerPage));
            }}
            styles={{ marginBottom: "6px" }}
          />

          {loading ? (
            <Typography>Loading orders...</Typography>
          ) : (
            <EntityList
              entityList={tableRows}
              columns={columns}
              onSortChange={handleSortChange}
              orderBy={orderBy}
              order={order}
              allowSelect
              onSelect={handleSelect}
              onSelectAll={handleSelectAll}
              selectedItems={selectedOrderIds}
              tableHeight="calc(100vh - 250px)"
            />
          )}
        </Box>
      </Page>

      <Dialog_YesNo
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title={
          selectedRecord?.latestSubmissionId ? "Resubmit Order" : "Submit Order"
        }
        message={
          selectedRecord?.latestSubmissionId
            ? `Resubmit order ${selectedRecord?.localId || ""} using latest submission snapshot?`
            : `Submit order ${selectedRecord?.localId || ""} as first transmission using active route/profile?`
        }
        onYes={() => {
          if (!resubmitting) {
            handleResubmit();
          }
        }}
        onNo={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default OrdersSubmissionsPage;
